import { Link, useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useMobileChrome } from './MobileChrome'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { AddNavIcon, CollectionNavIcon, HomeNavIcon, LogNavIcon, LayeringNavIcon, ProfileNavIcon, WearLogNavIcon } from './BottomNavIcons'

const TABS = [
  { to: '/', icon: HomeNavIcon, label: 'Home', ariaLabel: 'Home' },
  { to: '/collection', icon: CollectionNavIcon, label: 'Collection', ariaLabel: 'Collection' },
  { to: '/log', icon: LogNavIcon, label: 'Today', ariaLabel: "Today's wear" },
  { to: '/layering', icon: LayeringNavIcon, label: 'Layering', ariaLabel: 'Layering' },
  { to: '/profile', icon: ProfileNavIcon, label: 'Account', ariaLabel: 'Account' },
]

export function getBottomNavTabs(wearLogCleanupEnabled = false) {
  if (!wearLogCleanupEnabled) return TABS
  return TABS.map(tab => tab.to === '/log'
    ? { ...tab, icon: WearLogNavIcon, label: 'Wear Log', ariaLabel: 'Wear Log' }
    : tab)
}

export function NavBadge({ count, primary = false }) {
  if (!Number.isSafeInteger(count) || count <= 0) return null
  return <span data-nav-badge className={`absolute -right-2 -top-1 min-w-4 rounded-full border px-1 text-center text-[10px] font-bold leading-4 ${primary ? 'border-cream bg-charcoal-900 text-cream' : 'border-current'}`}>{count}</span>
}

// Counts are optional: no check-in-due signal exists yet. Never infer reminders from log totals.
export default function BottomNav({ badges = {}, signedIn = true }) {
  const wearLogCleanupEnabled = useFeatureFlag('wear_log_tab_cleanup')
  const { pathname } = useLocation()
  const { collapsed } = useMobileChrome()
  const navigate = useNavigate()
  function goHome() {
    window.scrollTo({ top: 0, behavior: 'instant' })
    if (!signedIn) { navigate('/login'); return }
    navigate('/')
  }
  function addFragrance() {
    window.scrollTo({ top: 0, behavior: 'instant' })
    if (!signedIn) { navigate('/login'); return }
    navigate('/', { state: { logAnother: true } })
  }
  const tabs = getBottomNavTabs(wearLogCleanupEnabled)
  const activeTab = tabs.find(tab => tab.to === '/' ? pathname === '/' : pathname.startsWith(tab.to)) || { to: null, icon: CollectionNavIcon, label: pathname === '/explore' ? 'Explore' : 'Navigation' }
  return createPortal(
    <div className="app-bottom-nav-viewport" data-bottom-nav-viewport>
      {collapsed ? <nav aria-label="Primary navigation" data-collapsed="true"
        className="app-bottom-nav app-bottom-nav--collapsed pointer-events-auto absolute z-40 flex items-center gap-4 rounded-full border border-white/10 bg-charcoal-900/95 px-4 py-1.5 shadow-xl shadow-black/40 backdrop-blur">
        <button type="button" onClick={goHome} aria-label="Home"
          className="relative flex min-h-11 items-center gap-1.5 px-1 text-xs font-medium text-gold"><HomeNavIcon /><span>Home</span><NavBadge count={badges['/']} /></button>
        <button type="button" onClick={addFragrance} aria-label="Add fragrance to today’s wear"
          className="relative flex min-h-11 items-center gap-1.5 rounded-full bg-gold px-3 text-xs font-semibold text-charcoal-900"><AddNavIcon /><span>Add</span></button>
      </nav> : <nav aria-label="Primary navigation" data-collapsed="false"
        className="app-bottom-nav pointer-events-auto absolute bottom-0 left-0 right-0 z-40 flex border-t border-white/5 bg-charcoal-900/95 backdrop-blur">
        {tabs.map(({ to, icon: Icon, label, ariaLabel }) =>
          <Link key={to} to={signedIn ? to : '/login'} aria-current={activeTab.to === to ? 'page' : undefined} aria-label={!signedIn && to === '/profile' ? 'Sign in' : ariaLabel}
            className={`relative flex min-h-11 flex-1 flex-col items-center justify-center gap-0.5 text-xs transition-colors ${activeTab.to === to ? 'text-gold' : 'text-cream/40 hover:text-cream/70'}`}>
            <span className="relative"><Icon /><NavBadge count={badges[to]} /></span><span className="font-medium">{!signedIn && to === '/profile' ? 'Sign in' : label}</span>
          </Link>)}
      </nav>}
    </div>,
    document.body,
  )
}
